<script setup lang="ts">
    import { onMounted, ref, reactive, computed } from 'vue';
    import * as DocModels from '../../lib/Models/DocumentModel';
    import { documents } from '../../lib/ChoiceDocumentApi';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import { ICol, RenderModeType, ValueStrDelegate, IRowSelection } from "../../lib/ChoiceTableHelpers";
    import SearchDocumentType from '../../components/Search/SearchDocumentType.vue';
    import { ISearchDocumentFolderItem, ISearchDocumentFolderKey, ISearchDocumentTypeItem, ISearchDocumentTypeKey, ISearchPatientItem, 
        IListByNameExtended, ISearchUserGroupItem} from '../../lib/Models/SearchModel';
    import SearchDocumentFolder from '../../components/Search/SearchDocumentFolder.vue'
    import * as Validation from "../../lib/Validation";
    import TextInput from '../../components/TextInput.vue'
    import SearchUserGroup from '../../components/Search/SearchUserGroup.vue';
    import * as UIEnums from '../../Enums/UiEnums';
    import * as ChoiceEnums from '../../Enums/ChoiceEnums';
    import DatabaseIdMessageBadge from '../../components/SelectionIndicators/DatabaseIdMessageBadge.vue';
    import SectionPanel from "../../components/SectionPanel.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { ModalWindowOpen } from '../../lib/ChoiceUserConfiguration';

    import { DisplayStyle, IconTypes } from '../../Enums/UiEnums';
    import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
    import Icon from '../../components/Icon.vue'
    import { confirmDialog } from '../../lib/Notification'

    const props = withDefaults(defineProps<{
        privilege: number,
    }>(), {
    });

    onMounted(async () => {    
        const setupApiKeyListPromise = loadApiKeyList();
        const setupReviewerListPromise = loadApiKeyReviewerList();
        await Promise.all([setupApiKeyListPromise, setupReviewerListPromise]); 
    });    

    const savePrivilege = computed<boolean>(() => props.privilege >= ChoiceEnums.PrivilegeType.ReadUpdateWrite && (selectedRowData.value?.sentinelApiKeyId ?? 0) > 0 ? true : false);
    const auditEnabled = computed<boolean>(() => (selectedRowData.value?.unsolicitedDocumentSetupId ?? 0) > 0);

    const apiKeyData = reactive<{
        apiKeyList?: DocModels.IUnsolicitedDocumentSetupApiKeyListResponse[]
    }>({});

    const reviewerData = reactive<{
        reviewerList?: DocModels.IUnsolicitedDocumentSetupReviewerListResponse[]
    }>({});
    
    const selectedReviewerData = ref<DocModels.IUnsolicitedDocumentSetupReviewerListResponse[]>();
    
    const formatFolderDesc: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: UIEnums.TableRowType): string | undefined => {
        if (rowType === UIEnums.TableRowType.Body)
        {
            if (null != row?.saveFolderIdDefault) {
                return `${row?.saveFolderDefaultDesc} (${row?.saveFolderIdDefault})`;                
            }
            else {
                return `${row?.saveFolderDefaultDesc}`;
            }
        }
    }

    const formatReviewerName: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: UIEnums.TableRowType): string | undefined => {
        if (rowType === UIEnums.TableRowType.Body)
        {
            const selectedReviewers = reviewerData.reviewerList?.filter(item => item.unsolicitedDocumentSetupId == row?.unsolicitedDocumentSetupId);
            const reviewerArray = Array();

            if (undefined != selectedReviewers) {
                for (const reviewer of selectedReviewers) {
                    reviewerArray.push(reviewer.reviewerDesc);
                }
                return `${reviewerArray.join("; ")}`;
            }
            else {
                return undefined;
            }            
        }
    }

    const tableColumns = ref<ICol[]>([
        { Name: 'Database', DataField: 'databaseName' },
        { Name: 'Webservice Account', DataField: 'webserviceAccountDesc' }, 
        { Name: 'Document Type', DataField: 'documentTypeDefaultDesc' },
        { Name: 'Save Location (ID)', DataField: 'saveFolderDefaultDesc', FormatValue: formatFolderDesc },
        { Name: 'eDocument Review Assignment', DataField: undefined, FormatValue: formatReviewerName },
        { Name: 'Filename', DataField: 'fileNameDefault' }
    ]);

    const selectedRowData = ref<DocModels.IUnsolicitedDocumentSetupApiKeyListResponse & IRowSelection>();

    const pageTitle = "Unsolicited Document Setup ";

    const screenDesc = `This screen defines the behavior and requirements for inbound documents coming from Webservice Accounts utilizing the document API. 
            Documents that DO NOT meet all defined conditions must be reconciled within My Tasks > eDocuments > Unsolicited queue.  Following the reconciliation
            of missing information, the eDocument may be saved to the patient's chart and accessed within the eDocuments.<br>NOTE: Unsolicited eDocuments are not 
            accessible externally by the Document API until reconciled.`;

    const formData = reactive<{
        sentinelApiKeyId: number,
        docTypeRequired: boolean,
        folderRequired: boolean,
        reviewRequired: boolean,
        fileNameRequired: boolean,
        selectedFileName?: string,
        defaultDocumentTypeKey?: ISearchDocumentTypeKey,
        defaultDocumentFolderKey?: ISearchDocumentFolderKey
        webServiceAccountInfo?: string,
    }>({
        sentinelApiKeyId: selectedRowData.value?.sentinelApiKeyId ?? 0,
        docTypeRequired: selectedRowData.value?.documentReviewRequired ?? false,
        folderRequired: selectedRowData.value?.saveFolderRequired ?? false,
        reviewRequired: selectedRowData.value?.documentReviewRequired ?? false,
        fileNameRequired: selectedRowData.value?.fileNameRequired ?? false,
        webServiceAccountInfo: undefined != selectedRowData.value?.databaseName ? selectedRowData.value?.databaseTitle + " " + selectedRowData.value?.databaseName : " --Select a webservice account below--"
    });

    const searchItemDocumentType = ref<ISearchDocumentTypeItem>();

    interface ISearchItem {
        searchResultItem?: ISearchPatientItem,
        folderItem?: ISearchDocumentFolderItem,
        displaySearchError: boolean,
        displayFolderError: boolean
    }

    //const defaultDocumentFolderKey = ref<ISearchDocumentFolderKey>();
    const searchItem = ref<ISearchItem>({displaySearchError: false, displayFolderError: false});

    async function loadApiKeyList() {
        const resp = await documents.getApiKeyDocumentSetupList();
        apiKeyData.apiKeyList = resp;
    }

    async function loadApiKeyReviewerList() {
        const resp = await documents.getApiKeyDocumentSetupReviewerList();
        reviewerData.reviewerList = resp;
    }

    function editDocumentSetup(rowData : IRowSelection, _rowType: UIEnums.TableRowType, _evt: MouseEvent) {
        selectedRowData.value = (rowData as DocModels.IUnsolicitedDocumentSetupApiKeyListResponse & IRowSelection);
        formData.sentinelApiKeyId = selectedRowData.value.sentinelApiKeyId;
        formData.docTypeRequired = selectedRowData.value?.documentTypeRequired ?? false;
        formData.folderRequired = selectedRowData.value?.saveFolderRequired ?? false;
        formData.reviewRequired = selectedRowData.value?.documentReviewRequired ?? false;
        formData.fileNameRequired = selectedRowData.value?.fileNameRequired ?? false;
        formData.webServiceAccountInfo = `${selectedRowData.value.databaseTitle} (${selectedRowData.value.databaseName}) - ${selectedRowData.value.webserviceAccountDesc}`;
        
        formData.defaultDocumentTypeKey = {
            documentTypeId: selectedRowData.value?.documentTypeIdDefault,
            display: selectedRowData.value.documentTypeDefaultDesc,
            searchText: selectedRowData.value.documentTypeDefaultDesc
            };
        
        formData.defaultDocumentFolderKey = {
            menuId: selectedRowData.value?.saveFolderIdDefault, 
            display: selectedRowData.value.saveFolderDefaultDesc, 
            searchText: selectedRowData.value.saveFolderDefaultDesc
        };

        formData.selectedFileName = selectedRowData.value.fileNameDefault;
        selectedReviewerData.value = reviewerData.reviewerList?.filter(item => item.unsolicitedDocumentSetupId == selectedRowData.value?.unsolicitedDocumentSetupId);
    }

    async function saveSetup() {
        const save_data = <DocModels.IUnsolicitedDocumentSetupSaveRequest>{
            unsolicitedDocumentSetupId: selectedRowData.value?.unsolicitedDocumentSetupId,
            sentinelApiKeyId: formData.sentinelApiKeyId,
            webServiceAccount: selectedRowData.value?.webserviceAccount,
            documentTypeRequired: formData.docTypeRequired,
            documentTypeIdDefault: searchItemDocumentType.value?.documentTypeId,
            saveFolderRequired: formData.folderRequired,
            saveFolderIdDefault: searchItem.value.folderItem?.folder_menu_id,
            documentReviewRequired: formData.reviewRequired,
            fileNameRequired: formData.fileNameRequired,
            fileNameDefault: formData.selectedFileName,
            reviewers: selectedReviewerData.value
        }

        const resp = await documents.saveDocumentSetup(save_data);
        await loadApiKeyList();
        await loadApiKeyReviewerList();
    }

    function onChangeSearchDocumentType(searchResultItem: ISearchDocumentTypeItem) {
        searchItemDocumentType.value = searchResultItem;
    }

    function onChangeFolder(folder: ISearchDocumentFolderItem, menuNameType: ChoiceEnums.MenuNameType) {
        searchItem.value.folderItem = folder;
        searchItem.value.displayFolderError = false;
    }

    function onSelectReviewedBy(selectedResult: ISearchUserGroupItem) {
        if (selectedResult) {
            if (undefined != selectedReviewerData.value) {
                //if user/group already exists in collection, don't add it again
                const userGroupExists = selectedReviewerData.value.some(item => item.reviewerId === selectedResult.identifier && item.reviewerTypeId === selectedResult.optionalIdentifier);
                if (userGroupExists) {
                    return;
                }
            }

            selectedReviewerData.value?.push({unsolicitedDocumentSetupId: selectedRowData.value?.unsolicitedDocumentSetupId, 
                reviewerTypeId: selectedResult.optionalIdentifier, reviewerId: selectedResult.identifier, reviewerDesc: selectedResult.display});
        }        
    }

    async function deleteReviewer(item: DocModels.IUnsolicitedDocumentSetupReviewerListResponse) {
        const confirmed = await confirmDialog(`Are you sure you want to delete this reviewer [${item.reviewerDesc}]?`, { RejectText: "Cancel", ConfirmText: "Confirm" });

        if (confirmed) {
            selectedReviewerData.value = selectedReviewerData.value?.filter(i => i.reviewerId !== item.reviewerId || i.reviewerTypeId !== item.reviewerTypeId);
        }
    }

    function convertUserGroupTypeToDatabaseIdType(type: ChoiceEnums.UserGroupType | undefined): DatabaseIdTypes | undefined {
        switch (type) {
            case ChoiceEnums.UserGroupType.User:
                return DatabaseIdTypes.user_id;
            case ChoiceEnums.UserGroupType.Group:
                return DatabaseIdTypes.user_group_id;
            default:
                return DatabaseIdTypes.user_id;
        }
    }

    async function openAudit() {
        ModalWindowOpen({screenID: ChoiceEnums.AppScreens.ChoiceAudit, vueModalOnly: true, urlParmData: {identifierId: selectedRowData.value?.unsolicitedDocumentSetupId, screenName: ChoiceEnums.AppScreens.UnsolicitedDocumentSetup}, title: "Unsolicited Document Setup Audit"});  
    }
    
</script>


<template>
    <div class="container-fluid">
        <div class="text-muted">{{ pageTitle }}</div>
        <div class="row mt-2 ps-2 pe-1" style="display: inline; font-size: 0.75rem;" v-html="screenDesc"></div>

        <div style="max-height: 500px; overflow-y: auto;">
        <SectionPanel style="width: 60%;">
            <div class="row mt-1">
                <div class="col text-isalus-primary fw-bold" style="font-size: 0.90rem;">
                    Webservice Account: {{ formData.webServiceAccountInfo }} 
                </div>
            </div>
            <div class="row mt-2 fw-bold label-isalus-small">
                <div class="col-md-3">
                    eDocument Field
                </div>
                <div class="col-md-2 text-center">
                    Required
                </div>
                <div class="col-md-7">
                    Default
                </div>
            </div>

            <div class="row label-isalus-small align-items-center">
                <div class="col-md-3">
                    Document Type
                </div>
                <div class="col col-md-2 text-center">
                    <input class="form-check-input me-1" v-model="formData.docTypeRequired" type="checkbox" :checked="selectedRowData?.documentTypeRequired" title="Check to require a document type be sent with all unsolicited documents">
                </div>
                <div class="col col-md-7">
                    <SearchDocumentType 
                        label=""
                        :addToBaseClass="'label-isalus-small'" 
                        :defaultSearchKey="formData.defaultDocumentTypeKey" 
                        placeholder="Search Document Types" 
                        :showClearSearch="true"
                        @onChange="(val) => onChangeSearchDocumentType(val)">
                    </SearchDocumentType>
                </div>
            </div>

            <div class="row label-isalus-small mt-3 align-items-center">
                <div class="col-md-3">
                    Save Folder
                </div>
                <div class="col-md-2 text-center">
                    <input class="form-check-input me-1" v-model="formData.folderRequired" type="checkbox" :checked="selectedRowData?.saveFolderRequired" title="Check to require a folder location be sent with all unsolicited documents">
                </div>
                <div class="col-md-7">
                    <SearchDocumentFolder 
                        label=""
                        :menuNameId="ChoiceEnums.MenuNameType.Patient" 
                        :defaultSearchKey="formData.defaultDocumentFolderKey" 
                        :addToBaseClass="'label-isalus-small'"
                        :showClearSearch="true"
                        placeholder="Search Patient Folders" 
                        @onChange="(val) => onChangeFolder(val, ChoiceEnums.MenuNameType.Patient)">
                    </SearchDocumentFolder>
                </div>
            </div>

            <div class="row label-isalus-small mt-3 align-items-center">
                <div class="col-md-3">
                    eDocument Review
                </div>
                <div class="col col-md-2 text-center">
                    <input class="form-check-input me-1" v-model="formData.reviewRequired" type="checkbox" :checked="selectedRowData?.documentReviewRequired" title="Check to require a reviewer be sent with all unsolicited documents">
                </div>
                <div class="col col-md-7">
                    <SearchUserGroup 
                        label=""
                        :screenName="ChoiceEnums.AppScreenNames.UnsolicitedDocumentSetup"
                        :addToBaseClass="'label-isalus-small'"
                        :showClearSearch="true"
                        @onChange="onSelectReviewedBy">
                    </SearchUserGroup>

                    <template v-if="selectedReviewerData">
                        <DatabaseIdMessageBadge v-for="item in selectedReviewerData" :idType="convertUserGroupTypeToDatabaseIdType(item.reviewerTypeId)">
                            {{ item.reviewerDesc }}
                            <Icon :icon="UIEnums.IconTypes.Delete" class="ms-2" :displayStyle="DisplayStyle.Info" title="Remove this User/Group" @click="() => deleteReviewer(item)"/>
                        </DatabaseIdMessageBadge>
                    </template>
                </div>
            </div>

            <div class="row label-isalus-small mt-3 align-items-center">
                <div class="col-md-3">
                    File Name
                </div>
                <div class="col-md-2 text-center">
                    <input class="form-check-input me-1" v-model="formData.fileNameRequired" type="checkbox" :checked="selectedRowData?.fileNameRequired" title="Check to require a file name be sent with all unsolicited documents">
                </div>
                <div class="col-md-7">
                    <TextInput 
                        v-model:text-value="formData.selectedFileName" 
                        :maxLength="100"
                        placeholder="Please enter a filename"
                        :acceptCharacter="Validation.LimitToFilenameCharacters"
                        :cssFramework = "'vanilla'">
                        <template #RightAddon>
                            .pdf
                        </template>
                    </TextInput>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-auto">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Save" 
                        text="Save"
                        :size="UIEnums.ButtonDisplaySize.Small"  
                        :disabled="!savePrivilege" 
                        :performAction="saveSetup" />
                </div>
                <div class="col-auto">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Audit" 
                        text="Audit"
                        :size="UIEnums.ButtonDisplaySize.Small"
                        :disabled="!auditEnabled"  
                        :performAction="openAudit" />
                </div>
            </div>
        </SectionPanel>
        </div>

        <div class="row pt-2">
            <ChoiceTable 
                :columns="tableColumns" 
                :data="apiKeyData.apiKeyList" 
                :showfilters="false" 
                :allowSorting="true"
                fontSize="smaller"
                key-value-data-member="apiKeyData.apiKeyList.unsolicitedDocumentSetupId"
                @rowclick="editDocumentSetup"                
            >
            </ChoiceTable>  
        </div>
    </div>

</template>